import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Space,
  Input,
  Divider,
  Typography,
  Spin,
  Tabs,
  Card,
} from 'antd';
import {
  CopyOutlined,
  MailOutlined,
  WhatsAppOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import {
  generateScheduleConfirmToken,
  sendScheduleConfirmationEmail,
  sendScheduleConfirmationWhatsapp,
} from '../../http/schedules';
import { IClient } from '../../shared/interfaces';
import { generateScheduleConfirmationUrl } from '../../shared/utils/schedule-confirmation-utils';
import { useI18n } from '../../shared/i18n';

const { Text } = Typography;

interface ISendConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  client?: IClient;
}

export const SendConfirmationModal: React.FC<ISendConfirmationModalProps> = ({
  open,
  onClose,
  scheduleId,
  client,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [email, setEmail] = useState(client?.email || '');
  const [phone, setPhone] = useState(client?.cell_phone || '');
  const [confirmationUrl, setConfirmationUrl] = useState('');

  useEffect(() => {
    if (open && !token) {
      handleGenerateToken();
    }
  }, [open]);

  const handleGenerateToken = async () => {
    setLoading(true);
    try {
      const response = await generateScheduleConfirmToken(scheduleId);
      setToken(response.token);
      const baseUrl =
        process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';
      const url = generateScheduleConfirmationUrl(
        scheduleId,
        response.token,
        baseUrl,
      );
      setConfirmationUrl(url);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.errorTitle',
        ),
        description: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.tokenError',
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(confirmationUrl);
    addToast({
      type: 'success',
      title: t(
        'scheduleConfirmationMessageModal.sendConfirmationModal.linkCard.copiedTitle',
      ),
      description: t(
        'scheduleConfirmationMessageModal.sendConfirmationModal.linkCard.copiedDescription',
      ),
    });
  };

  const handleSendEmail = async () => {
    if (!email) {
      addToast({
        type: 'error',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.errorTitle',
        ),
        description: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.emailRequired',
        ),
      });
      return;
    }

    setLoadingEmail(true);
    try {
      await sendScheduleConfirmationEmail({
        schedule_id: scheduleId,
        email,
        token,
      });
      addToast({
        type: 'success',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.successTitle',
        ),
        description: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.emailSent',
        ),
      });
      onClose();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.errorTitle',
        ),
        description:
          error?.response?.data?.message ||
          t(
            'scheduleConfirmationMessageModal.sendConfirmationModal.toast.emailError',
          ),
      });
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!phone) {
      addToast({
        type: 'error',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.errorTitle',
        ),
        description: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.phoneRequired',
        ),
      });
      return;
    }

    setLoadingWhatsapp(true);
    try {
      await sendScheduleConfirmationWhatsapp({
        schedule_id: scheduleId,
        phone,
        link: confirmationUrl,
        client_name: client?.corporate_name || 'Cliente',
      });
      addToast({
        type: 'success',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.successTitle',
        ),
        description: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.whatsappSent',
        ),
      });
      onClose();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t(
          'scheduleConfirmationMessageModal.sendConfirmationModal.toast.errorTitle',
        ),
        description:
          error?.response?.data?.message ||
          t(
            'scheduleConfirmationMessageModal.sendConfirmationModal.toast.whatsappError',
          ),
      });
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  const items = [
    {
      key: 'email',
      label: (
        <span>
          <MailOutlined />{' '}
          {t(
            'scheduleConfirmationMessageModal.sendConfirmationModal.tabs.email',
          )}
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text style={{ display: 'block', marginBottom: 8 }}>
              {t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.fields.email',
              )}
            </Text>
            <Input
              type="email"
              placeholder={t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.fields.emailPlaceholder',
              )}
              prefix={<MailOutlined />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loadingEmail}
              size="large"
            />
          </div>
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleSendEmail}
            loading={loadingEmail}
            disabled={!email || loadingEmail || !token}
            size="large"
            block
          >
            {loadingEmail
              ? t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.actions.sendEmailLoading',
                )
              : t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.actions.sendEmail',
                )}
          </Button>
        </Space>
      ),
    },
    {
      key: 'whatsapp',
      label: (
        <span>
          <WhatsAppOutlined />{' '}
          {t(
            'scheduleConfirmationMessageModal.sendConfirmationModal.tabs.whatsapp',
          )}
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text style={{ display: 'block', marginBottom: 8 }}>
              {t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.fields.phone',
              )}
            </Text>
            <Input
              placeholder={t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.fields.phonePlaceholder',
              )}
              prefix={<WhatsAppOutlined />}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loadingWhatsapp}
              size="large"
            />
            <Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, display: 'block' }}
            >
              {t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.fields.phoneHelp',
              )}
            </Text>
          </div>
          <Button
            type="primary"
            icon={<WhatsAppOutlined />}
            onClick={handleSendWhatsapp}
            loading={loadingWhatsapp}
            disabled={!phone || loadingWhatsapp || !token}
            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            size="large"
            block
          >
            {loadingWhatsapp
              ? t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.actions.sendWhatsappLoading',
                )
              : t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.actions.sendWhatsapp',
                )}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={t('scheduleConfirmationMessageModal.sendConfirmationModal.title', {
        name: client?.corporate_name || '',
      })}
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('scheduleConfirmationMessageModal.sendConfirmationModal.close')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} />}
      >
        {token && confirmationUrl ? (
          <>
            {/* Link de Confirmação */}
            <Card
              size="small"
              style={{ marginBottom: 24, backgroundColor: '#fafafa' }}
              bordered={false}
            >
              <Text strong>
                {t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.linkCard.title',
                )}
              </Text>
              <Divider style={{ margin: '12px 0' }} />
              <Input.TextArea
                value={confirmationUrl}
                readOnly
                rows={2}
                style={{ marginBottom: 12, fontSize: 12 }}
              />
              <Button
                type="dashed"
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                size="small"
                block
              >
                {t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.linkCard.copy',
                )}
              </Button>
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: 12, fontSize: 12 }}
              >
                {t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.linkCard.expires',
                )}
              </Text>
            </Card>

            {/* Abas de Envio */}
            <Tabs items={items} defaultActiveKey="email" />

            <Divider />

            <Card
              size="small"
              style={{
                backgroundColor: '#e6f7ff',
                borderColor: '#91d5ff',
              }}
              bordered
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  'scheduleConfirmationMessageModal.sendConfirmationModal.info',
                )}
              </Text>
            </Card>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin
              spinning={loading}
              indicator={<LoadingOutlined style={{ fontSize: 48 }} />}
            />
            <Text style={{ display: 'block', marginTop: 16 }}>
              {t(
                'scheduleConfirmationMessageModal.sendConfirmationModal.loading',
              )}
            </Text>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default SendConfirmationModal;
